<section class="dashboard-page">
  <header class="page-topbar dashboard-header">
    <div class="topbar-title-block">
      <h1 class="page-title">Dashboard</h1>
      <p class="page-subtitle">Track documents that need your attention and keep tabs on sending activity.</p>
    </div>
    <button type="button" class="refresh-btn btn-purple" (click)="loadSummary()" [disabled]="loading">
      <svg class="refresh-icon" [class.spinning]="loading" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"></path>
      </svg>
      <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
    </button>
  </header>

  <div class="dashboard-body">

  @if (errorMessage) {
    <div class="status-banner error">
      <p>{{ errorMessage }}</p>
    </div>
  }

  <div class="dashboard-content-grid">
    <!-- Left Column: Document Activity stats + Document Tabs Card -->
    <div class="dashboard-left-col">
      <div class="activity-section">
        <h3 class="section-title">Document Activity</h3>
        <!-- Single outer bordered wrapper matching Figma -->
        <div class="document-activity-wrapper">
          <div class="document-activity-grid">
            <!-- Total Volume Card -->
            <article class="stat-card total-volume-card">
              <div class="stat-header-row">
                <div class="stat-main-info">
                  <h2 class="stat-value">{{ metricCard('totalDocuments').total }}</h2>
                  <p class="stat-label">TOTAL VOLUME</p>
                </div>
                
                <div class="stat-side-info">
                  <span class="badge badge-light-gray">ALL TIME</span>
                  <div class="stat-breakdown">
                    <span class="breakdown-item sent-item">
                      <span class="breakdown-icon sent" aria-hidden="true">
                        <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="4" y1="16" x2="16" y2="4"></line>
                          <polyline points="7 4 16 4 16 13"></polyline>
                        </svg>
                      </span>
                      Sent: <strong>{{ metricCard('totalDocuments').sent }}</strong>
                    </span>
                    <span class="breakdown-item received-item">
                      <span class="breakdown-icon received" aria-hidden="true">
                        <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="16" y1="4" x2="4" y2="16"></line>
                          <polyline points="13 16 4 16 4 7"></polyline>
                        </svg>
                      </span>
                      Received: <strong>{{ metricCard('totalDocuments').received }}</strong>
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="stat-proportion-bar">
                <div class="proportion-fill sent" [style.width.%]="metricCard('totalDocuments').total > 0 ? (metricCard('totalDocuments').sent / metricCard('totalDocuments').total) * 100 : 50"></div>
                <div class="proportion-fill received" [style.width.%]="metricCard('totalDocuments').total > 0 ? (metricCard('totalDocuments').received / metricCard('totalDocuments').total) * 100 : 50"></div>
              </div>
            </article>

            <!-- This Month Card -->
            <article class="stat-card this-month-card">
              <div class="stat-header-row">
                <div class="stat-main-info">
                  <h2 class="stat-value">{{ metricCard('thisMonthDocuments').total }}</h2>
                  <p class="stat-label">THIS MONTH</p>
                </div>
                
                <div class="stat-side-info">
                  <span class="badge badge-light-gray">{{ getCurrentMonthLabel() }}</span>
                  <div class="stat-breakdown">
                    <span class="breakdown-item sent-item">
                      <span class="breakdown-icon sent" aria-hidden="true">
                        <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="4" y1="16" x2="16" y2="4"></line>
                          <polyline points="7 4 16 4 16 13"></polyline>
                        </svg>
                      </span>
                      Sent: <strong>{{ metricCard('thisMonthDocuments').sent }}</strong>
                    </span>
                    <span class="breakdown-item received-item">
                      <span class="breakdown-icon received" aria-hidden="true">
                        <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="16" y1="4" x2="4" y2="16"></line>
                          <polyline points="13 16 4 16 4 7"></polyline>
                        </svg>
                      </span>
                      Received: <strong>{{ metricCard('thisMonthDocuments').received }}</strong>
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="stat-proportion-bar">
                <div class="proportion-fill sent" [style.width.%]="metricCard('thisMonthDocuments').total > 0 ? (metricCard('thisMonthDocuments').sent / metricCard('thisMonthDocuments').total) * 100 : 50"></div>
                <div class="proportion-fill received" [style.width.%]="metricCard('thisMonthDocuments').total > 0 ? (metricCard('thisMonthDocuments').received / metricCard('thisMonthDocuments').total) * 100 : 50"></div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <!-- Central Document Tabs Card -->
      <article class="document-tabs-card">
        <header class="tabs-header">
          @for (section of sections; track section.key) {
            <button
              type="button"
              [class]="'tab-btn tab-btn-' + section.key"
              [class.active]="activeTab === section.key"
              (click)="setActiveTab(section.key)"
            >
              <span class="tab-icon-wrapper">
                @switch (section.key) {
                  @case ('waitingForMe') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 20h9"></path>
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                    </svg>
                  }
                  @case ('waitingForOthers') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  }
                  @case ('needsAttention') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                      <line x1="12" y1="9" x2="12" y2="13"></line>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                  }
                  @case ('recentlyCompleted') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  }
                }
              </span>
              <span class="tab-label">{{ section.title }}</span>
              <span class="tab-badge">{{ sectionData(section.key).count | number:'2.0-0' }}</span>
            </button>
          }
        </header>

        <div class="tabs-content-body">
          @if (loading) {
            <div class="c-loading-state tabs-loading-wrapper">
              <div class="c-spinner"></div>
              <p class="c-loading-text">Loading dashboard data…</p>
            </div>
          } @else if (sectionData(activeTab).items.length === 0) {
            <div class="tabs-empty-state">
              <div class="empty-illustration-circle">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
              </div>
              <p class="empty-title">{{ activeSection.emptyTitle }}</p>
              <p class="empty-copy">{{ activeSection.emptyMessage }}</p>
            </div>
          } @else {
            <div class="document-items-list" role="list">
              @for (item of sectionData(activeTab).items; track item.id) {
                <div class="document-item-row" role="listitem">
                  <button
                    type="button"
                    class="item-info-btn"
                    (click)="handleSectionItemClick(activeSection, item)"
                  >
                    <div class="item-status-indicator" [class]="'indicator-' + activeTab"></div>
                    <div class="item-text-group">
                      <span class="item-title">{{ item.title }}</span>
                      <span class="item-subtitle">{{ subtitleFor(activeSection, item) }}</span>
                    </div>
                  </button>
                  <div class="item-right-actions">
                    <span class="item-meta">{{ timestampLabelFor(activeSection, item) }}</span>
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <footer class="tabs-card-footer">
          <a
            class="view-all-btn"
            [routerLink]="['/documents']"
            [queryParams]="viewAllQueryParams(activeSection)"
          >
            <span>View All</span>
            <svg class="chevron-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </a>
        </footer>
      </article>
    </div>

    <!-- Right Column: Workplace Health + Live Activity list -->
    <div class="dashboard-right-col">
      <!-- Workplace Health -->
      <article class="right-card health-card">
        <h3 class="right-card-title">Workplace Health</h3>
        <div class="health-progress-section">
          <div class="progress-bar-container">
            <div class="progress-bar-fill" [style.width.%]="getWorkplaceHealthScore()"></div>
          </div>
          <span class="health-percentage">{{ getWorkplaceHealthScore() }}%</span>
        </div>
        <p class="health-description">
          <strong>{{ sectionData('needsAttention').count | number:'2.0-0' }}</strong> docs need your attention
        </p>
      </article>

      <!-- Live Activity Feed -->
      <article class="right-card live-activity-card">
        <header class="live-activity-header">
          <h3 class="right-card-title">Live Activity</h3>
          <span class="live-badge">
            <span class="live-dot"></span>
            <span>Live</span>
          </span>
        </header>

        <div class="live-activity-body">
          @if (loading) {
            <p class="live-loading">Loading activity feed…</p>
          } @else if (getLiveActivities().length === 0) {
            <div class="live-empty-state">
              <p>No recent activity detected.</p>
            </div>
          } @else {
            <div class="live-events-list">
              @for (item of getLiveActivities(); track item.id) {
                <div class="live-event-row">
                  <div class="event-icon-wrapper" [class]="'event-' + item.action">
                    @switch (item.action) {
                      @case ('signed') {
                        <!-- Pencil icon -->
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M12 20h9"></path>
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                        </svg>
                      }
                      @case ('viewed') {
                        <!-- Eye icon -->
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      }
                      @case ('sent') {
                        <!-- Send icon -->
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="22" y1="2" x2="11" y2="13"></line>
                          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                      }
                      @case ('created') {
                        <!-- Plus Document icon -->
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                        </svg>
                      }
                    }
                  </div>
                  <div class="event-details">
                    <p class="event-text">
                      <span class="event-action" [class]="'action-' + item.action">{{ item.action | titlecase }}</span>
                      <strong class="event-doc-title">{{ item.title }}</strong>
                    </p>
                    <p class="event-actor-meta">by {{ item.actor }}</p>
                    <p class="event-time">{{ item.timeLabel }}</p>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </article>
    </div>
  </div>
  </div>
</section>
